import { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Trash2,
  Star,
  Globe,
  Pencil,
  Share2,
  Tv,
  Factory,
  Users,
} from "lucide-react";

import { useContextMenu } from "../../../contexts/RightClickMenuContext";
import { usePopup } from "../../../contexts/PopupContext";
import { useLayout } from "../../../contexts/LayoutContext";
import { useCompanyContext } from "../../../../context";
import { useSearchRegistry } from "../../Search/SearchRegistry";
import ShareModal from "../ShareModal";
import SharedWithMeSection from "../SharedWithMeSection";
import { usePortalTarget, feedViewIcon } from "./sidebarUtils";

// Hardcoded feed views — workaround until getFeedViews returns these.
// Each entry maps to its own endpoint in api/feed; the sidebar fires
// the call directly on click and dispatches the payload to the feed page.

const FeedSidebarPortal = () => {
  const portalTarget = usePortalTarget("feed-portal-slot");
  const { currentAppPage, leftOpen } = useLayout();
  const { currentCompany } = useCompanyContext();
  const navigate = useNavigate();

  const [systemViews, setSystemViews] = useState([]);
  const [customViews, setCustomViews] = useState([]);
  const [favoritedSources, setFavoritedSources] = useState([]);
  const [viewOrder, setViewOrder] = useState([]);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [activeView, setActiveView] = useState({
    kind: "system",
    key: "Global",
  });
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const { setIsPopUpOpen, setPopUpComponent } = usePopup();

  // Clear active state when another section becomes active
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section && e.detail.section !== "feed") {
        setActiveView({ kind: null, key: null });
      }
    };
    window.addEventListener("sidebar:active-section-changed", handler);
    return () =>
      window.removeEventListener("sidebar:active-section-changed", handler);
  }, []);

  const loadData = useCallback(async () => {
    try {
      const { getFeedViews } = await import("../../../../api/feed");
      const res = await getFeedViews();
      const d = res?.data || {};
      if (d.system_views) setSystemViews(d.system_views);
      if (d.custom_views) setCustomViews(d.custom_views);
      if (d.favorited_sources) setFavoritedSources(d.favorited_sources);
      if (d.view_order) setViewOrder(d.view_order);
    } catch (e) {
      console.error("Error loading feed views:", e);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const handler = () => loadData();
    window.addEventListener("sidebar:refresh-feed-views", handler);
    return () =>
      window.removeEventListener("sidebar:refresh-feed-views", handler);
  }, [loadData]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail) setActiveView(e.detail);
    };
    window.addEventListener("feed:active-view-changed", handler);
    return () =>
      window.removeEventListener("feed:active-view-changed", handler);
  }, []);

  /* ── Breadcrumb: emit active sub-item ── */
  const emitFeedBreadcrumb = useCallback(() => {
    if (currentAppPage !== "/feed") return;
    let label = null;
    if (activeView.kind === "system") label = activeView.key;
    else if (activeView.kind === "hardcoded") label = activeView.label;
    else if (activeView.kind === "favorited_source") label = activeView.source;
    else if (activeView.kind === "custom") label = activeView.view?.name;
    window.dispatchEvent(
      new CustomEvent("breadcrumb:update", {
        detail: {
          section: "/feed",
          subLabel: label,
          subIcon: null,
          hasAdd: true,
          addKind: "feed",
          addLabel: "Add feed view",
        },
      }),
    );
  }, [activeView, currentAppPage]);

  useEffect(() => {
    emitFeedBreadcrumb();
  }, [emitFeedBreadcrumb, leftOpen]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section === "/feed") emitFeedBreadcrumb();
    };
    window.addEventListener("breadcrumb:request", handler);
    return () => window.removeEventListener("breadcrumb:request", handler);
  }, [emitFeedBreadcrumb]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section === "/feed") {
        if (currentAppPage !== "/feed") navigate("/feed");
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("sidebar:feed-open-create-modal"),
          );
        }, 50);
      }
    };
    window.addEventListener("breadcrumb:add", handler);
    return () => window.removeEventListener("breadcrumb:add", handler);
  }, [currentAppPage, navigate]);

  /* ── Build unified ordered list ── */
  const orderedItems = useMemo(() => {
    const itemMap = new Map();

    systemViews.forEach((v) => {
      const id = `system:${v.key}`;
      itemMap.set(id, { orderId: id, kind: "system", data: v });
    });

    favoritedSources.forEach((f) => {
      const id = `fav:${f.document_id}`;
      itemMap.set(id, { orderId: id, kind: "favorited_source", data: f });
    });

    customViews.forEach((v) => {
      const id = `custom:${v.document_id}`;
      itemMap.set(id, { orderId: id, kind: "custom", data: v });
    });

    if (viewOrder.length > 0) {
      const result = [];
      const used = new Set();
      viewOrder.forEach((id) => {
        if (itemMap.has(id)) {
          result.push(itemMap.get(id));
          used.add(id);
        }
      });
      // Append any items not in the saved order (newly created)
      itemMap.forEach((item, id) => {
        if (!used.has(id)) result.push(item);
      });
      return result;
    }

    return [...itemMap.values()];
  }, [systemViews, customViews, favoritedSources, viewOrder]);

  /* ── Search registry ── */
  const { register, unregister } = useSearchRegistry();
  useEffect(() => {
    register(
      "/feed",
      orderedItems.map((item) => {
        if (item.kind === "system") {
          const v = item.data;
          return {
            id: item.orderId,
            label: v.label,
            keywords: ["feed", "view"],
            icon: feedViewIcon(v.icon),
            iconColor: v.color || "rgba(14,165,233,0.70)",
            event: {
              navigateTo: "/feed",
              name: "sidebar:feed-switch-view",
              detail: { kind: "system", view: v },
            },
          };
        }
        if (item.kind === "hardcoded") {
          const v = item.data;
          return {
            id: item.orderId,
            label: v.label,
            keywords: ["feed", "view", v.key],
            icon: v.icon,
            iconColor: v.color,
            event: {
              navigateTo: "/feed",
              name: "sidebar:feed-switch-view",
              detail: { kind: "hardcoded", view: v },
            },
          };
        }
        if (item.kind === "favorited_source") {
          const f = item.data;
          return {
            id: item.orderId,
            label: f.source_label,
            keywords: ["feed", "favorite", "source"],
            icon: Globe,
            iconColor: "rgba(14,165,233,0.85)",
            event: {
              navigateTo: "/feed",
              name: "sidebar:feed-switch-view",
              detail: {
                kind: "favorited_source",
                document_id: f.document_id,
                source: f.source_value,
              },
            },
          };
        }
        if (item.kind === "custom") {
          const v = item.data;
          return {
            id: item.orderId,
            label: v.name,
            keywords: ["feed", "view", "custom"],
            icon: feedViewIcon(v.icon),
            iconColor: v.color || "rgba(148,163,184,0.60)",
            event: {
              navigateTo: "/feed",
              name: "sidebar:feed-switch-view",
              detail: { kind: "custom", view: v },
            },
          };
        }
        return { id: item.orderId, label: "Unknown", keywords: [] };
      }),
    );
    return () => unregister("/feed");
  }, [orderedItems, register, unregister]);

  const goToFeed = () => {
    if (currentAppPage !== "/feed") navigate("/feed");
  };

  const handleSystemViewClick = (view) => {
    goToFeed();
    // Highlight right away — the feed page confirms once it has switched
    setActiveView({ kind: "system", key: view.key, view });
    window.dispatchEvent(
      new CustomEvent("sidebar:active-section-changed", {
        detail: { section: "feed" },
      }),
    );
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("sidebar:feed-switch-view", {
          detail: { kind: "system", view },
        }),
      );
    }, 50);
  };

  const handleHardcodedClick = (view) => {
    goToFeed();

    const nextActive = { kind: "hardcoded", key: view.key, label: view.label };
    setActiveView(nextActive);
    window.dispatchEvent(
      new CustomEvent("feed:active-view-changed", { detail: nextActive }),
    );
    window.dispatchEvent(
      new CustomEvent("sidebar:active-section-changed", {
        detail: { section: "feed" },
      }),
    );

    // Re-assert after the feed page initializes
    setTimeout(() => {
      setActiveView(nextActive);
      window.dispatchEvent(
        new CustomEvent("feed:active-view-changed", { detail: nextActive }),
      );
    }, 200);

    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("sidebar:feed-switch-view", {
          detail: { kind: "hardcoded", view },
        }),
      );
    }, 50);
  };

  const handleFavoriteClick = (fav) => {
    goToFeed();
    setActiveView({
      kind: "favorited_source",
      document_id: fav.document_id,
      source: fav.source_value,
    });
    window.dispatchEvent(
      new CustomEvent("sidebar:active-section-changed", {
        detail: { section: "feed" },
      }),
    );
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("sidebar:feed-switch-view", {
          detail: {
            kind: "favorited_source",
            document_id: fav.document_id,
            source: fav.source_value,
          },
        }),
      );
    }, 50);
  };

  const handleViewClick = (view) => {
    goToFeed();
    setActiveView({ kind: "custom", document_id: view.document_id, view });
    window.dispatchEvent(
      new CustomEvent("sidebar:active-section-changed", {
        detail: { section: "feed" },
      }),
    );
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("sidebar:feed-switch-view", {
          detail: { kind: "custom", view },
        }),
      );
    }, 50);
  };

  const handleAddView = () => {
    goToFeed();
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("sidebar:feed-open-create-modal"));
    }, 50);
  };

  const confirmDeleteView = async (documentId) => {
    try {
      const { deleteFeedView } = await import("../../../../api/feed");
      await deleteFeedView({ document_id: documentId });
      loadData();
      window.dispatchEvent(new CustomEvent("sidebar:refresh-feed-views"));
    } catch (err) {
      console.error("Delete view failed:", err);
    } finally {
      setPendingDeleteId(null);
    }
  };

  /* ── Context menus ── */
  useContextMenu("feed-custom-view", [
    {
      label: "Share",
      icon: Share2,
      action: (payload) => {
        setPopUpComponent(
          <ShareModal
            onClose={() => setIsPopUpOpen(false)}
            contentType="custom_feed_view_v2"
            documentId={payload.document_id}
            itemName={payload.name}
          />,
        );
        setIsPopUpOpen(true);
      },
    },
    {
      label: "Edit View",
      icon: Pencil,
      action: (payload) => {
        goToFeed();
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("sidebar:feed-open-edit-modal", {
              detail: { view: payload },
            }),
          );
        }, 50);
      },
    },
    {
      label: "Delete View",
      icon: Trash2,
      variant: "danger",
      action: (payload) => {
        setPendingDeleteId(payload.document_id);
      },
    },
  ]);

  useContextMenu("feed-favorited-source", [
    {
      label: "Remove Favorite",
      icon: Star,
      variant: "danger",
      action: async (payload) => {
        try {
          const { unfavoriteFeedSource } = await import("../../../../api/feed");
          await unfavoriteFeedSource({ document_id: payload.document_id });
          loadData();
          window.dispatchEvent(new CustomEvent("sidebar:refresh-feed-views"));
        } catch (err) {
          console.error("Unfavorite failed:", err);
        }
      },
    },
  ]);

  /* ── Drag and drop ── */
  const handleDragStart = (e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
    if (e.currentTarget) {
      e.dataTransfer.setDragImage(e.currentTarget, 0, 0);
    }
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (idx !== dragOverIdx) setDragOverIdx(idx);
  };

  const handleDragLeave = () => {
    setDragOverIdx(null);
  };

  const handleDrop = async (e, dropIdx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === dropIdx) {
      setDragIdx(null);
      setDragOverIdx(null);
      return;
    }

    const items = [...orderedItems];
    const [moved] = items.splice(dragIdx, 1);
    items.splice(dropIdx, 0, moved);

    const newOrder = items.map((item) => item.orderId);
    setViewOrder(newOrder);
    setDragIdx(null);
    setDragOverIdx(null);

    try {
      const { saveFeedViewOrder } = await import("../../../../api/feed");
      await saveFeedViewOrder({ order: newOrder });
    } catch (err) {
      console.error("Save order failed:", err);
    }
  };

  const handleDragEnd = () => {
    setDragIdx(null);
    setDragOverIdx(null);
  };

  const rowClasses = (isActive) =>
    [
      "w-full text-left rounded-md px-2 py-1.5 text-[12px] truncate transition-all duration-200 flex items-center gap-x-1.5 group/row",
      isActive
        ? "text-[#0EA5E9] bg-[#0EA5E9]/[0.08] font-medium"
        : "text-zinc-500 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800/40",
    ].join(" ");

  const renderItem = (item, idx) => {
    const isDragging = dragIdx === idx;
    const isDragOver = dragOverIdx === idx;

    const dragProps = {
      draggable: true,
      onDragStart: (e) => handleDragStart(e, idx),
      onDragOver: (e) => handleDragOver(e, idx),
      onDragLeave: handleDragLeave,
      onDrop: (e) => handleDrop(e, idx),
      onDragEnd: handleDragEnd,
    };

    const dragStyle = {
      opacity: isDragging ? 0.35 : 1,
      borderTop:
        isDragOver && dragIdx !== null && dragIdx > idx
          ? "2px solid rgba(14,165,233,0.5)"
          : undefined,
      borderBottom:
        isDragOver && dragIdx !== null && dragIdx < idx
          ? "2px solid rgba(14,165,233,0.5)"
          : undefined,
      cursor: "grab",
    };

    if (item.kind === "system") {
      const view = item.data;
      const Icon = feedViewIcon(view.icon);
      const isActive =
        activeView.kind === "system" && activeView.key === view.key;
      return (
        <div key={item.orderId} style={dragStyle} {...dragProps}>
          <button
            onClick={() => handleSystemViewClick(view)}
            className={rowClasses(isActive)}
          >
            <Icon
              className="h-3 w-3 shrink-0"
              strokeWidth={2}
              style={{ color: view.color || "rgba(14,165,233,0.70)" }}
            />
            <span className="truncate">{view.label}</span>
          </button>
        </div>
      );
    }

    if (item.kind === "hardcoded") {
      const view = item.data;
      const Icon = view.icon;
      const isActive =
        activeView.kind === "hardcoded" && activeView.key === view.key;
      return (
        <div key={item.orderId} style={dragStyle} {...dragProps}>
          <button
            onClick={() => handleHardcodedClick(view)}
            className={rowClasses(isActive)}
          >
            <Icon
              className="h-3 w-3 shrink-0"
              strokeWidth={2}
              style={{ color: view.color }}
            />
            <span className="truncate">{view.label}</span>
          </button>
        </div>
      );
    }

    if (item.kind === "favorited_source") {
      const fav = item.data;
      const isActive =
        activeView.kind === "favorited_source" &&
        activeView.document_id === fav.document_id;
      return (
        <div
          key={item.orderId}
          style={dragStyle}
          {...dragProps}
          data-context-menu="feed-favorited-source"
          data-context-payload={JSON.stringify(fav)}
        >
          <button
            onClick={() => handleFavoriteClick(fav)}
            className={rowClasses(isActive)}
          >
            <Globe
              className="h-3 w-3 shrink-0"
              strokeWidth={2}
              style={{ color: "rgba(14,165,233,0.85)" }}
            />
            <span className="truncate flex-1">{fav.source_label}</span>
            <Star
              className="h-3 w-3 shrink-0 text-amber-400 opacity-60"
              strokeWidth={2}
              fill="currentColor"
            />
          </button>
        </div>
      );
    }

    if (item.kind === "custom") {
      const view = item.data;
      const isActive =
        activeView.kind === "custom" &&
        activeView.document_id === view.document_id;
      const ViewIcon = feedViewIcon(view.icon);
      const isPendingDelete = pendingDeleteId === view.document_id;
      return (
        <div
          key={item.orderId}
          style={dragStyle}
          {...dragProps}
          data-context-menu="feed-custom-view"
          data-context-payload={JSON.stringify(view)}
        >
          <button
            onClick={() => handleViewClick(view)}
            className={rowClasses(isActive)}
          >
            <ViewIcon
              className="h-3 w-3 shrink-0"
              strokeWidth={2}
              style={{ color: view.color || "rgba(148,163,184,0.60)" }}
            />
            <span className="truncate flex-1">{view.name}</span>
          </button>

          <div
            className={[
              "overflow-hidden transition-all duration-200 ease-in-out",
              isPendingDelete
                ? "max-h-[80px] opacity-100 mt-0.5 mb-0.5"
                : "max-h-0 opacity-0",
            ].join(" ")}
          >
            {isPendingDelete && (
              <div className="flex items-center gap-x-2 py-1 px-1">
                <span className="text-[11px] text-slate-500 dark:text-gray-400">
                  Delete?
                </span>
                <button
                  onClick={() => setPendingDeleteId(null)}
                  className="text-[11px] px-2 py-0.5 rounded border border-slate-300 dark:border-slate-600 text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => confirmDeleteView(view.document_id)}
                  className="text-[11px] px-2 py-0.5 rounded border border-red-300 dark:border-red-500/30 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                >
                  Confirm
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  const isInNavbar = portalTarget?.dataset?.navbar === "true";

  const content = (
    <div className="flex flex-col gap-y-px">
      {!isInNavbar && (
        <button
          onClick={handleAddView}
          className="group w-full flex items-center gap-x-2 rounded-md px-2 py-1.5 text-[12px] text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800/60 transition-colors"
          title="Add feed view"
        >
          <Plus className="h-3 w-3 shrink-0" strokeWidth={2.5} />
          <span>Add feed view</span>
        </button>
      )}

      {orderedItems.map((item, idx) => renderItem(item, idx))}
      <SharedWithMeSection
        contentType="feeds"
        onSelect={(item) => handleViewClick(item)}
      />
    </div>
  );

  if (!portalTarget) return null;
  return createPortal(content, portalTarget);
};

export default FeedSidebarPortal;
